import { PortalError } from "@/types/portal-error";
import { Service } from "../mainService";
import { FiltrosContratos } from "@/types/licitacoes/contratos/filtros-contratos";
import { Contrato } from "@/types/licitacoes/contratos/contrato";
import { DadosComplementaresCrtLicOutraUg } from '@/types/licitacoes/contratos/dados-complementares-ctr-lic-outra-ug'
import { ParticipanteContrato } from "@/types/licitacoes/contratos/participante-contrato";
import { RetornoImportarContratos } from "@/types/licitacoes/contratos/retornos/retorno-importar-contratos";

const contratoService = {
  //lista contratos
  filtrarCOFs,

  //dados iniciais
  buscarDadosGerais,
  salvarDadosIniciais,
  baixarAnexo,
  buscarHistorico,

  //importar xml
  importarContratos,
  baixarRelatorioInconsistencias,

  //anular
  anular,
  desfazerAnulacao,

  //participantes
  buscarParticipantes,
  adicionarParticipanteContrato,
  removerParticipanteContrato,
  checarParticipanteUg,
  habilitarConsorcio,

  //auxiliares
  getCombos,
  listarTiposContrato,
  listarProcedimentosOrigem,
  buscarSituacao,
  buscarDadosComplementaresCrtLicOutraUg,
  buscarDadosLicitacaoPropria,
  listarLicitacoesUG,
  listarLicitacoesOutraUG,
  listarUgsMunicipio,

  //remover contrato
  removerContrato,

  //auxiliares internacionais
  getListaTldPaises,
};

async function filtrarCOFs(
  page: number,
  pageSize: number,
  filtros: FiltrosContratos
): Promise<any> {
  try {
    const url = `${process.env.VUE_APP_LICITACOES_API
      }/contrato/filtrarContratos?page=${page}&pageSize=${pageSize}${filtros.toQueryString()}`;
    const response = await Service.$http.get(url);
    if (response && response.status === 200) {
      return response.data;
    } else {
      console.error("Falha ao filtrarContratos", response);
      throw new PortalError(
        response.data?.msg ||
        "Ocorreu um erro ao realizar a consulta. Verifique se os filtros de pesquisa aplicados podem ser refinados!"
      );
    }
  } catch (e: any) {
    console.error("Falha ao filtrar contratos", e);
    throw new PortalError(e.response.data.msg ?? e);
  }
}

async function listarTiposContrato() {
  try {
    const url =
      process.env.VUE_APP_LICITACOES_API + "/contrato/tiposContrato/";
    const response = await Service.$http.get(url);
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function listarProcedimentosOrigem() {
  try {
    const url =
      process.env.VUE_APP_LICITACOES_API + "/contrato/procedimentosOrigem/";
    const response = await Service.$http.get(url);
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function buscarSituacao(id: number): Promise<string> {
  const url = `${process.env.VUE_APP_LICITACOES_API}/contrato/buscarSituacao/${id}`;
  const response = await Service.$http.get(url);
  if (response && response.status === 200) {
    return response.data.CdSituacao;
  } else {
    console.error(response);
    throw new PortalError("Falha ao obter status do contrato.");
  }
}

async function buscarDadosGerais(idContrato: string): Promise<Contrato> {
  try {
    const url =
      process.env.VUE_APP_LICITACOES_API +
      "/contrato/buscarDadosGerais/" +
      idContrato;
    const response = await Service.$http.get(url);
    const contrato = Object.assign(new Contrato(), response.data.contrato);
    return contrato;
  } catch (e: any) {
    return e.response.data;
  }
}

async function buscarDadosComplementaresCrtLicOutraUg(idUg: number, idModalidade: number): Promise<DadosComplementaresCrtLicOutraUg> {
  try {
    const url = `${process.env.VUE_APP_LICITACOES_API}/contrato/buscarDadosContratoLicitacaoOutraUg/${idUg}/${idModalidade}`;
    const response = await Service.$http.get(url);
    const dadosComplementares = Object.assign(new DadosComplementaresCrtLicOutraUg(), response.data);
    return dadosComplementares;
  } catch (e: any) {
    return e.response.data;
  }
}

async function buscarDadosLicitacaoPropria(idLicitacao: number): Promise<string> {
  try {
    const url = `${process.env.VUE_APP_LICITACOES_API}/contrato/buscarDadosLicitacaoPropria/${idLicitacao}`;
    const response = await Service.$http.get(url);
    const dadosLicitacaoPropria = response.data;
    return dadosLicitacaoPropria;
  } catch (e: any) {
    return e.response.data;
  }
}

async function baixarAnexo(id: string) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_LICITACOES_API + "/contrato/baixaranexo/" + id,
      { responseType: "blob" }
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function buscarParticipantes(idContrato: string, page: number, pageSize: number): Promise<any> {
  try {
    const url =
      process.env.VUE_APP_LICITACOES_API + `/contrato/buscarParticipantes/${idContrato}?page=${page}&pageSize=${pageSize}`;
    const response = await Service.$http.get(url);
    if (response && response.status === 200) return response.data;
    else {
      console.error(
        "Falha ao buscar participantes do contrato",
        response
      );
      return null;
    }
  } catch (e: any) {
    return e.response.data;
  }
}

async function buscarHistorico(
  idContrato: number,
  page: number,
  size: number
): Promise<any> {
  try {
    const url = `${process.env.VUE_APP_LICITACOES_API}/contrato/buscarLogs/${idContrato}?page=${page}&pageSize=${size}`;
    const response = await Service.$http.get(url);
    if (response && response.status === 200) return response.data;
    else {
      console.error("Falha ao buscar histórico do contrato", response);
      return null;
    }
  } catch (e: any) {
    return e.response;
  }
}

async function getCombos() {
  //tipos, procedimentos de origem
  try {
    const [rTipo, rProc] = await Promise.all([
      Service.$http.get(
        process.env.VUE_APP_LICITACOES_API +
        "/contrato/tiposContrato"
      ),
      Service.$http.get(
        process.env.VUE_APP_LICITACOES_API + "/contrato/procedimentosOrigem"
      )
    ]);
    return {
      tipos: rTipo.data,
      procedimentos: rProc.data,
    };
  } catch (e: any) {
    return e.response;
  }
}

/**
 * Add
 * @param Licitacao lic
 * @returns Licitacao licitacao salva
 * @since 20230428
 */
async function salvarDadosIniciais(ctr: Contrato): Promise<Contrato> {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_LICITACOES_API + "/contrato/salvarDadosGerais",
      ctr.toFormData()
    );
    if (response.status === 200) {
      const contrato = Object.assign(new Contrato(), response.data.contrato);
      contrato.tratarDadosDaAPI();
      return contrato;
    }
    throw new PortalError("Falha ao salvar dados iniciais do contrato");
  } catch (e: any) {
    console.error("capotou no service", e);
    if ([400, 403].includes(e.response?.status)) {
      throw montarArrayDeErros(e);
    }
    throw e;
  }
}

async function anular(ctr: Contrato): Promise<any> {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_LICITACOES_API + `/contrato/anularContrato/${ctr.IdContrato}`,
      ctr.toFormData()
    );
    if (response.status === 200) {
      return true;
    }
    throw new PortalError("Falha ao anular contrato");
  } catch (e: any) {
    console.error("capotou no service", e);
    if ([400, 403].includes(e.response?.status)) {
      throw montarArrayDeErros(e);
    }
    throw e;
  }
}

async function desfazerAnulacao(idContrato: number): Promise<any> {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_LICITACOES_API + `/contrato/desfazerAnulacao/${idContrato}`,
    );
    if (response.status === 200) {
      return true;
    }
    throw new PortalError("Falha ao desfazer anulação do contrato");
  } catch (e: any) {
    console.error("capotou no service", e);
    if ([400, 403].includes(e.response?.status)) {
      throw montarArrayDeErros(e);
    }
    throw e;
  }
}

async function adicionarParticipanteContrato(
  pc: ParticipanteContrato
): Promise<ParticipanteContrato> {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_LICITACOES_API + "/contrato/salvarParticipante",
      pc.toFormData()
    );
    const novoPart = Object.assign(new ParticipanteContrato(), response.data);
    return novoPart;
  } catch (e: any) {
    console.error("Erro ao tentar salvar novo participante", e.response);
    if ([400, 403].includes(e.response.status)) {
      throw new PortalError(e.response.data.erros ?? e.response.data.msg);
    } else {
      throw e;
    }
  }
}

async function removerParticipanteContrato(idPC: number): Promise<boolean> {
  try {
    const response = await Service.$http.delete(
      process.env.VUE_APP_LICITACOES_API +
      "/contrato/removerParticipante/" +
      idPC
    );
    return response.status === 204;
  } catch (e: any) {
    console.error("Erro ao tentar remover participante do contrato", e);
    if ([400, 403].includes(e.response.status)) {
      throw new PortalError(e.response.data.erros ?? e.response.data.msg);
    } else {
      throw e;
    }
  }
}

async function habilitarConsorcio(id: number): Promise<any> {
  try {
    const url = `${process.env.VUE_APP_LICITACOES_API}/contrato/habilitarConsorcio/${id}`;
    const response = await Service.$http.get(url);
    return response.data;
  } catch (e: any) {
    console.error("Erro ao tentar consultar dados", e);
    if ([400, 403].includes(e.response.status)) throw new PortalError(e.response.data.erros ?? e.response.data.msg);
    throw e;
  }
}

async function removerContrato(id: number): Promise<boolean> {
  try {
    const url = `${process.env.VUE_APP_LICITACOES_API}/contrato/removerContrato/${id}`;
    const response = await Service.$http.delete(url);
    return response.status === 204;
  } catch (e: any) {
    console.error("Erro ao tentar remover contrato", e);
    if ([400, 403].includes(e.response.status)) throw new PortalError(e.response.data.erros ?? e.response.data.msg);
    throw e;
  }
}

async function listarLicitacoesUG() {
  try {
    const url =
      process.env.VUE_APP_LICITACOES_API + "/contrato/listarLicitacoesUG/";
    const response = await Service.$http.get(url);
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function listarLicitacoesOutraUG(codUg: number) {
  try {
    const url =
      process.env.VUE_APP_LICITACOES_API + "/contrato/listarLicitacoesOutraUg/" + codUg;
    const response = await Service.$http.get(url);
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function listarUgsMunicipio() {
  try {
    const url =
      process.env.VUE_APP_LICITACOES_API + "/contrato/listarUgsMunicipio/";
    const response = await Service.$http.get(url);
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function importarContratos(
  arquivo: File
): Promise<RetornoImportarContratos> {
  try {
    const url = process.env.VUE_APP_LICITACOES_API + "/contrato/importar/xml";
    const formData = new FormData();
    formData.append("zipFile", arquivo);
    const response = await Service.$http.post(url, formData);
    //console.log("ldiService.importarLicitacoes recebeu", response);
    if (response && response.status === 200)
      return new RetornoImportarContratos(response.data);
    throw new PortalError(response.data.msg ?? "Erro ao importar contratos");
  } catch (e: any) {
    if (e.response?.status === 400 && e.response?.data)
      return new RetornoImportarContratos(e.response.data);
    throw new PortalError(e.response.data.msg ?? "Erro ao importar contratos");
  }
}

async function baixarRelatorioInconsistencias(
  ril: RetornoImportarContratos
): Promise<Blob> {
  const response = await Service.$http.post(
    process.env.VUE_APP_LICITACOES_API +
    "/contrato/relatoriodeinconsistencias",
    ril,
    { responseType: "blob" }
  );
  //console.log("ldiService.baixarRelatorioInconsistencias recebeu", response);
  if (response && response.status === 200) return response.data;
  console.error(
    "falha ao baixar relatório de inconsistências de importação de contratos",
    response
  );
  throw new PortalError(response.data.msg ?? response);
}

async function checarParticipanteUg(cnpj: string) {
  try {
    const url =
      process.env.VUE_APP_LICITACOES_API + "/contrato/checarParticipanteUg/" + cnpj;
    const response = await Service.$http.get(url);
    return response;
  } catch (e: any) {
    return e.response;
  }
}

function montarArrayDeErros(e: any): PortalError {
  if (e.response.data.erros) return new PortalError(e.response.data.erros);
  if (e.response.data.msg) return new PortalError(e.response.data.msg);
  return new PortalError(e);
}

async function getListaTldPaises(): Promise<{ Sigla: string; Descricao: string }[]> {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_LICITACOES_API + "/contrato/getListaTldPaises"
    );
    if (response && response.status === 200) return response.data;
    return [];
  } catch (e: any) {
    console.error("Erro ao buscar lista de TLD de países", e);
    return [];
  }
}

export default contratoService;
